import React, { useState } from 'react';
import {
	Card,
	CardContent,
	Button,
	FormField,
	Input,
	Alert,
	AlertTitle,
	Dropdown,
	DropdownItem,
	Spinner,
	PlatformMark
} from '../ui';
import { Cross1Icon } from '@radix-ui/react-icons';
import { useScanCreate } from '../features/scans';
import { ScanService } from '../../services';

/**
 * ScanCreateForm - Composite component for creating new scans
 * 
 * Follows the design system principles with clean separation of concerns:
 * - Uses UI primitives for consistent styling
 * - Uses business logic hook for data management
 * - Handles form state and validation
 * - Provides proper error handling and loading states
 */
export function ScanCreateForm({ initialData, onScanCreated, onCancel }) {
	const [keywordInput, setKeywordInput] = useState("");

	const {
		// Form data
		formData,
		selectedClientId,
		selectedAccountId,
		availableAccounts,

		// Loading states
		loading,
		accountsLoading,

		// Error states
		error,
		validationErrors,

		// Data
		clients,
		selectedClient,
		selectedAccount,
		platforms,
		sensitivityLevels,
		riskCategories,

		// Actions
		updateFormData,
		setClientId,
		setAccountId,
		addKeyword,
		removeKeyword,
		toggleCategory,
		createScan,

		// Computed
		isFormValid,
		estimatedDuration,
	} = useScanCreate(initialData);

	const handleSubmit = async (e) => {
		e.preventDefault();

		try {
			const result = await createScan();
			if (result && onScanCreated) {
				onScanCreated(result);
			}
		} catch (error) {
			// Error is handled by the hook
		}
	};

	const handleAddKeyword = () => {
		if (keywordInput.trim()) {
			addKeyword(keywordInput);
			setKeywordInput("");
		}
	};

	const formatCreationDate = (account) => {
		// Prioritize platform creation date over RepCheck creation date
		const creationDate = account.creationDate || account.createdAt;
		const isPlatformDate = !!account.creationDate;

		const formattedDate = new Date(creationDate).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});

		// Add indicator if it's the platform creation date
		return isPlatformDate ? `Created: ${formattedDate}` : `Added: ${formattedDate}`;
	};

	return (
		<Card className="max-w-4xl mx-auto">
			<CardContent className="p-6">
				<form onSubmit={handleSubmit} className="space-y-6">
					{/* Client and Account Selection */}
					<div className="bg-muted rounded-lg p-4 space-y-4">
						<h3 className="text-lg font-medium text-foreground">Select Client & Account</h3>

						{/* Client Selection */}
						<FormField label="Client" required>
							<Dropdown
								trigger={
									<Button type="button" variant="outline" className="w-full justify-between">
										<span className={selectedClientId ? "text-foreground" : "text-muted-foreground"}>
											{selectedClient?.name || "Select a client"}
										</span>
										<svg className="h-5 w-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
										</svg>
									</Button>
								}
								align="left"
								className="w-full"
							>
								{clients.length > 0 ? (
									clients.map((client) => (
										<DropdownItem
											key={client._id}
											onClick={() => setClientId(client._id)}
										>
											<div className="flex flex-col">
												<span className="font-medium">{client.name}</span>
												<span className="text-xs text-muted-foreground">{client.email}</span>
											</div>
										</DropdownItem>
									))
								) : (
									<div className="px-3 py-2 text-sm text-muted-foreground">
										No clients available
									</div>
								)}
							</Dropdown>
						</FormField>

						{/* Account Selection */}
						<FormField label="Account" required>
							{accountsLoading ? (
								<div className="flex items-center justify-center p-4 border border-input rounded-md bg-muted">
									<Spinner size="sm" className="mr-2" />
									<span className="text-sm text-muted-foreground">Loading accounts...</span>
								</div>
							) : (
								<Dropdown
									trigger={
										<Button
											type="button"
											variant="outline"
											className="w-full justify-between"
											disabled={!selectedClientId}
										>
											<span className={selectedAccountId ? "text-foreground" : "text-muted-foreground"}>
												{selectedAccount
													? `@${selectedAccount.handle}`
													: selectedClientId
														? "Select an account"
														: "Please select a client first"
												}
											</span>
											<svg className="h-5 w-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
											</svg>
										</Button>
									}
									align="left"
									className="w-full"
								>
									{availableAccounts.length > 0 ? (
										availableAccounts.map((account) => (
											<DropdownItem
												key={account._id}
												onClick={() => setAccountId(account._id)}
											>
												<div className="flex flex-col">
													<span className="font-medium">@{account.handle}</span>
													<span className="text-xs text-muted-foreground">{account.platform}</span>
													<span className="text-xs text-muted-foreground">
														{formatCreationDate(account)}
													</span>
												</div>
											</DropdownItem>
										))
									) : selectedClientId ? (
										<div className="px-3 py-2 text-sm text-muted-foreground">
											No accounts available for this client
										</div>
									) : (
										<div className="px-3 py-2 text-sm text-muted-foreground">
											Please select a client first
										</div>
									)}
								</Dropdown>
							)}
						</FormField>
					</div>

					{/* Platform Display */}
					{selectedAccount && (
						<FormField label="Platform">
							<div className="border border-input rounded-lg p-4 bg-muted">
								<div className="flex items-center space-x-3">
									{(() => {
										const platformInfo = platforms.find(p => p.value === selectedAccount.platform.toLowerCase());
										return (
											<>
												<PlatformMark platform={selectedAccount?.platform} size="md" />
												<div>
													<div className="font-medium text-foreground">{platformInfo?.label || selectedAccount.platform}</div>
													<div className="text-sm text-muted-foreground">Auto-selected based on account</div>
												</div>
											</>
										);
									})()}
								</div>
							</div>
						</FormField>
					)}

					{/* Username Display */}
					{selectedAccount && (
						<FormField label="Username">
							<div className="px-3 py-2 border border-input rounded-md bg-muted text-foreground">
								{formData.username}
								<p className="text-xs text-muted-foreground mt-1">
									Auto-filled from selected account
								</p>
							</div>
						</FormField>
					)}

					{/* Date Range */}
					<FormField label="Date Range">
						{/* Preset Buttons */}
						<div className="mb-3 flex flex-wrap gap-2">
							<Button
								type="button"
								variant={formData.datePreset === '7days' ? 'primary' : 'outline'}
								size="sm"
								onClick={() => {
									const endDate = new Date();
									const startDate = new Date();
									startDate.setDate(endDate.getDate() - 7);
									updateFormData({
										datePreset: '7days',
										startDate: startDate.toISOString().split('T')[0],
										endDate: endDate.toISOString().split('T')[0]
									});
								}}
							>
								Last 7 Days
							</Button>
							<Button
								type="button"
								variant={formData.datePreset === '30days' ? 'primary' : 'outline'}
								size="sm"
								onClick={() => {
									const endDate = new Date();
									const startDate = new Date();
									startDate.setDate(endDate.getDate() - 30);
									updateFormData({
										datePreset: '30days',
										startDate: startDate.toISOString().split('T')[0],
										endDate: endDate.toISOString().split('T')[0]
									});
								}}
							>
								Last 30 Days
							</Button>
							<Button
								type="button"
								variant={formData.datePreset === '90days' ? 'primary' : 'outline'}
								size="sm"
								onClick={() => {
									const endDate = new Date();
									const startDate = new Date();
									startDate.setDate(endDate.getDate() - 90);
									updateFormData({
										datePreset: '90days',
										startDate: startDate.toISOString().split('T')[0],
										endDate: endDate.toISOString().split('T')[0]
									});
								}}
							>
								Last 90 Days
							</Button>
							{selectedAccount && selectedAccount.creationDate && (
								<Button
									type="button"
									variant={formData.datePreset === 'accountCreation' ? 'primary' : 'outline'}
									size="sm"
									onClick={() => {
										const endDate = new Date();
										const startDate = new Date(selectedAccount.creationDate);
										updateFormData({
											datePreset: 'accountCreation',
											startDate: startDate.toISOString().split('T')[0],
											endDate: endDate.toISOString().split('T')[0]
										});
									}}
								>
									Since Account Creation
								</Button>
							)}
							<Button
								type="button"
								variant={formData.datePreset === 'custom' ? 'primary' : 'outline'}
								size="sm"
								onClick={() => updateFormData({ datePreset: 'custom' })}
							>
								Custom Range
							</Button>
						</div>

						{/* Date Inputs */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-foreground mb-1">Start Date</label>
								<Input
									type="date"
									value={formData.startDate}
									onChange={(e) => updateFormData({ startDate: e.target.value, datePreset: 'custom' })}
									required
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-foreground mb-1">End Date</label>
								<Input
									type="date"
									value={formData.endDate}
									onChange={(e) => updateFormData({ endDate: e.target.value, datePreset: 'custom' })}
									required
								/>
							</div>
						</div>
					</FormField>

					{/* Estimated Duration */}
					{estimatedDuration && (
						<div className="bg-brand-50 border border-brand-200 rounded-lg p-3 dark:bg-brand-500/10 dark:border-brand-500/30">
							<div className="flex items-center">
								<svg className="h-5 w-5 text-brand-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								<span className="text-sm text-brand-800">
									Estimated duration: ~{Math.ceil(estimatedDuration / 60)} min
									{(() => {
										const windows = ScanService.estimateScanWindows(formData);
										if (!windows) return null;
										return (
											<>
												{' '}· {windows} search window{windows === 1 ? '' : 's'}
												{ScanService.usesPaidCredits(formData.platform) && ' · uses metered API credits'}
											</>
										);
									})()}
								</span>
							</div>
						</div>
					)}

					{/* Sensitivity Level */}
					<FormField label="Sensitivity Level">
						<div className="space-y-2">
							{sensitivityLevels.map((level) => (
								<label key={level.value} className="flex items-center">
									<input
										type="radio"
										name="sensitivityLevel"
										value={level.value}
										checked={formData.sensitivityLevel === level.value}
										onChange={(e) => updateFormData({ sensitivityLevel: e.target.value })}
										className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-input"
									/>
									<div className="ml-3">
										<div className="text-sm font-medium text-foreground">{level.label}</div>
										<div className="text-xs text-muted-foreground">{level.description}</div>
									</div>
								</label>
							))}
						</div>
					</FormField>

					{/* Risk Categories */}
					<FormField label="Risk Categories to Monitor">
						<div className="grid grid-cols-2 md:grid-cols-3 gap-2">
							{riskCategories.map((category) => (
								<label key={category.value} className="flex items-center">
									<input
										type="checkbox"
										checked={formData.enabledCategories.includes(category.value)}
										onChange={() => toggleCategory(category.value)}
										className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-input rounded"
									/>
									<span className="ml-2 text-sm text-foreground">{category.label}</span>
								</label>
							))}
						</div>
					</FormField>

					{/* Custom Keywords */}
					<FormField label="Custom Keywords (Optional)">
						<div className="space-y-2">
							<div className="flex space-x-2">
								<Input
									value={keywordInput}
									onChange={(e) => setKeywordInput(e.target.value)}
									onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddKeyword())}
									placeholder="Add keyword..."
									className="flex-1"
								/>
								<Button type="button" onClick={handleAddKeyword}>
									Add
								</Button>
							</div>
							{formData.customKeywords.length > 0 && (
								<div className="flex flex-wrap gap-2">
									{formData.customKeywords.map((keyword, index) => (
										<span
											key={index}
											className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-muted text-foreground"
										>
											{keyword}
											<button
												type="button"
												onClick={() => removeKeyword(keyword)}
												className="ml-1 text-muted-foreground hover:text-muted-foreground"
											>
												<Cross1Icon className="h-3 w-3" />
											</button>
										</span>
									))}
								</div>
							)}
						</div>
					</FormField>

					{/* Validation Errors */}
					{validationErrors.length > 0 && (
						<Alert variant="destructive">
							<AlertTitle>Please fix the following errors:</AlertTitle>
							<ul className="mt-2 space-y-1">
								{validationErrors.map((error, index) => (
									<li key={index} className="text-sm">• {error}</li>
								))}
							</ul>
						</Alert>
					)}

					{/* General Error */}
					{error && (
						<Alert variant="destructive">
							<AlertTitle>Error</AlertTitle>
							<p>{error}</p>
						</Alert>
					)}

					{/* Submit Button */}
					<div className="flex justify-end space-x-3">
						{onCancel && (
							<Button type="button" variant="outline" onClick={onCancel}>
								Cancel
							</Button>
						)}
						<Button
							type="submit"
							disabled={loading || !isFormValid}
							className="flex items-center"
						>
							{loading && <Spinner size="sm" className="mr-2" />}
							{loading ? "Creating Scan..." : "Start Scan"}
						</Button>
					</div>
				</form>
			</CardContent>
		</Card>
	);
} 